import {
  loadGooglePlacesLibrary,
  readGoogleMapsAPIKey,
  type GooglePlacePrediction,
  type GooglePlacesLibrary,
} from "./google-places";
import {
  looksLikeSpecificAddress,
  type TargetLocationSuggestion,
} from "./target-location";

export interface TargetLocationProvider {
  search(query: string): Promise<TargetLocationSuggestion[]>;
}

const TARGET_LOCATION_REGION_CODES = ["us"];
// City, neighborhood, and ZIP only (plan 454): a state or county cannot be
// placed at one ZIP, so Google is never asked for them.
const TARGET_LOCATION_PRIMARY_TYPES = [
  "locality",
  "sublocality",
  "neighborhood",
  "postal_code",
];
// Belt and braces for the request filter above: Google can still return a
// prediction whose `types` include a state or county.
const EXCLUDED_PREDICTION_TYPES = new Set([
  "administrative_area_level_1",
  "administrative_area_level_2",
]);
const TARGET_LOCATION_LOOKUP_RETRY_DELAY_MS = 150;
const providers = new Map<string, TargetLocationProvider>();

export type TargetLocationProviderErrorCode =
  | "configuration"
  | "load"
  | "quota"
  | "unavailable";

export class TargetLocationProviderError extends Error {
  code: TargetLocationProviderErrorCode;

  constructor(code: TargetLocationProviderErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "TargetLocationProviderError";
  }
}

export function getTargetLocationProvider(): TargetLocationProvider | null {
  const apiKey = readGoogleMapsAPIKey();
  if (!apiKey) {
    return null;
  }
  const cached = providers.get(apiKey);
  if (cached) {
    return cached;
  }
  const provider = new GooglePlacesTargetLocationProvider(apiKey);
  providers.set(apiKey, provider);
  return provider;
}

class GooglePlacesTargetLocationProvider implements TargetLocationProvider {
  private readonly apiKey: string;
  private sessionToken: unknown | null = null;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  async search(query: string) {
    const trimmed = query.trim();
    if (!trimmed || looksLikeSpecificAddress(trimmed)) {
      return [];
    }

    try {
      return await this.searchOnce(trimmed);
    } catch (error) {
      const providerError = toTargetLocationProviderError(error);
      if (providerError.code !== "load") {
        throw providerError;
      }

      await sleep(TARGET_LOCATION_LOOKUP_RETRY_DELAY_MS);

      try {
        return await this.searchOnce(trimmed);
      } catch (retryError) {
        throw toTargetLocationProviderError(retryError);
      }
    }
  }

  private ensureSessionToken(library: GooglePlacesLibrary) {
    if (!this.sessionToken) {
      this.sessionToken = new library.AutocompleteSessionToken();
    }
  }

  private async searchOnce(trimmed: string) {
    const library = await loadGooglePlacesLibrary(this.apiKey);
    this.ensureSessionToken(library);
    const response =
      await library.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        includedPrimaryTypes: TARGET_LOCATION_PRIMARY_TYPES,
        includedRegionCodes: TARGET_LOCATION_REGION_CODES,
        input: trimmed,
        sessionToken: this.sessionToken ?? undefined,
      });

    return dedupeTargetLocations(
      response.suggestions.flatMap((suggestion) => {
        const prediction = suggestion.placePrediction;
        if (!prediction) {
          return [];
        }
        const targetLocation = targetLocationFromPrediction(prediction);
        return targetLocation ? [targetLocation] : [];
      }),
    );
  }
}

function targetLocationFromPrediction(
  prediction: GooglePlacePrediction,
): TargetLocationSuggestion | null {
  const fullText = prediction.text.toString();
  const mainText = prediction.mainText?.toString() ?? fullText;

  if (prediction.types?.some((type) => EXCLUDED_PREDICTION_TYPES.has(type))) {
    return null;
  }
  if (
    looksLikeSpecificAddress(fullText) ||
    looksLikeSpecificAddress(mainText)
  ) {
    return null;
  }

  const label = stripCountrySuffix(fullText);
  if (!label || !prediction.placeId) {
    return null;
  }
  // Google's own text, not a reformatted "Ballard, WA": the server resolves by
  // place_id, and the label is what the buyer sees and what is stored.
  return {
    label,
    kind: "place",
    place_id: prediction.placeId,
    mainText: mainText.trim(),
  };
}

function stripCountrySuffix(value: string) {
  return value.replace(/,\s*(?:USA|United States)$/i, "").trim();
}

function dedupeTargetLocations(locations: TargetLocationSuggestion[]) {
  const seen = new Set<string>();
  return locations.filter((location) => {
    const key = location.place_id ?? `${location.kind}:${location.label}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function toTargetLocationProviderError(error: unknown) {
  if (error instanceof TargetLocationProviderError) {
    return error;
  }
  const providerCode =
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "number"
      ? error.code
      : null;
  const message = error instanceof Error ? error.message : "Unknown error";
  const normalized = message.toLowerCase();
  if (
    providerCode === 7 ||
    normalized.includes("permission_denied") ||
    normalized.includes("request_denied") ||
    normalized.includes("not been used in project") ||
    normalized.includes("disabled") ||
    normalized.includes("referer")
  ) {
    return new TargetLocationProviderError(
      "configuration",
      "Google Places API access is not enabled for this browser key.",
    );
  }
  if (
    normalized.includes("quota") ||
    normalized.includes("billing") ||
    normalized.includes("over_query_limit")
  ) {
    return new TargetLocationProviderError(
      "quota",
      "Google Places is temporarily unavailable.",
    );
  }
  if (
    normalized.includes("load") ||
    normalized.includes("network") ||
    normalized.includes("maps")
  ) {
    return new TargetLocationProviderError(
      "load",
      "Google Places failed to load.",
    );
  }
  return new TargetLocationProviderError(
    "unavailable",
    "Google Places is unavailable right now.",
  );
}
